import type { OpportunityStatus } from '@ps/web-sports-api-types';
import { RunnerStatusMarketDetails } from '@ps/web-sports-api-types';
import { isEqual } from 'lodash';

import {
  sportsCompetition,
  sportsEvent,
  sportsMarket,
  sportsSelection,
  sportsSport
} from '../entities/index';
import type { Entity } from '../entities/index';
import { buttonClick } from '../events';
import type { TrackingContextValue } from '../providers/TrackingContext';
import { GAEvent } from '../utils';

import { getIsAvailableOpportunity, getIsSingleAvailableOpportunity } from './getIsOpportunity';
import isOddsBoostMarket from './isOddsBoostMarket';
import mergeWithTrackingContextValue from './mergeWithTrackingContextValue';
import type { SelectionRemovedProps } from './types';

export const selectionRemoved = (
  context: TrackingContextValue,
  { singleOpportunities, runnerId }: SelectionRemovedProps
) => {
  const runnerStatusMap: Record<OpportunityStatus, RunnerStatusMarketDetails> = {
    AVAILABLE: RunnerStatusMarketDetails.ACTIVE,
    SUSPENDED: RunnerStatusMarketDetails.SUSPENDED,
    UNAVAILABLE: RunnerStatusMarketDetails.REMOVED,
    RESTRICTED: RunnerStatusMarketDetails.ACTIVE
  };
  const opportunity = singleOpportunities.find((opp) => isEqual(opp.runnerId, runnerId));
  if (opportunity) {
    const entities: Entity[] = [
      sportsEvent({
        eventId: opportunity.eventId,
        eventStartTime: getIsSingleAvailableOpportunity(opportunity)
          ? new Date(opportunity.eventStartTime).getTime()
          : undefined
      }),
      sportsMarket({
        marketId: runnerId.marketId,
        marketInPlay: getIsAvailableOpportunity(opportunity) ? opportunity.eventInPlay : false,
        marketIsOddsBoost: getIsAvailableOpportunity(opportunity)
          ? isOddsBoostMarket(opportunity.marketType)
          : false
      }),
      sportsSelection({
        selectionId: runnerId.selectionId,
        selectionOdds: getIsAvailableOpportunity(opportunity)
          ? opportunity.winRunnerOdds?.decimalDisplayOdds?.decimalOdds
          : undefined,
        selectionStatus: runnerStatusMap[opportunity.status],
        isStartingPrice: false
      })
    ];

    const { eventTypeId, competitionId } = opportunity;

    if (eventTypeId) {
      entities.push(sportsSport({ sportId: eventTypeId }));
    }

    if (competitionId) {
      entities.push(sportsCompetition({ competitionId }));
    }
    context = mergeWithTrackingContextValue(context, entities);

    GAEvent({ action: 'Remove Selection' }, context);
    buttonClick({ buttonName: 'Remove Selection' }, context);
  }
};
